/**
 * Presence indicators - renders bubbles showing connected users over the banner area.
 */
import {
	CharacterImageVariant,
	ImageContainerShape,
	renderCharacterImage,
} from '@datapad/common-controls';
import { type PresenceUser, useAppContext, usePresenceContext } from '@datapad/data-model';
import { Person, Security } from '@mui/icons-material';
import { Avatar, Box, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { usePresence } from '../hooks/index.js';
import { PresenceTooltip, useResolvedPresenceUser } from './PresenceUtilities.js';

// Bubble dimensions
const bubbleSize = 25;

/**
 * Gap (in px) between adjacent bubbles when the container has enough room.
 */
const bubbleGap = 8;

/**
 * Minimum width (in px) each bubble slot can shrink to before clipping.
 * Controls how much overlap is allowed when space is tight.
 */
const minSlotWidth = bubbleSize - 10;

/**
 * Padding (in px) inside the outer container. This creates space between the
 * `overflow: hidden` clip edge and the bubble content so that outlines,
 * borders, and hover-scale transforms are not clipped.
 */
const containerPadding = 5;

/**
 * Font size (in px) for the counter text inside the collapsed indicator.
 */
const counterFontSize = 16;

/**
 * Renders presence bubbles for all connected users in a facepile layout.
 *
 * @remarks
 * Bubbles are laid out in a flex row with natural spacing. When the viewport
 * is too narrow to fit all bubbles, the flex slots shrink and fixed-size
 * bubbles overlap. Hovering a bubble raises its z-index so it pops over
 * any neighbours that would otherwise cover it.
 */
export function PresenceIndicators(): React.ReactElement {
	const remoteUsers = usePresence();
	const manager = usePresenceContext();
	const myself = manager.getMyself();

	const totalUsers = remoteUsers.length + 1;
	const allUsers = [myself, ...remoteUsers];

	const [isCollapsed, setIsCollapsed] = React.useState(false);
	const [isHovered, setIsHovered] = React.useState(false);

	React.useEffect(() => {
		function check(): void {
			const maxWidth = window.innerWidth - 120;
			setIsCollapsed(totalUsers > 1 && totalUsers * minSlotWidth > maxWidth);
		}
		check();
		window.addEventListener('resize', check);
		return () => window.removeEventListener('resize', check);
	}, [totalUsers]);

	const expandedContent = totalUsers * (bubbleSize + bubbleGap);
	const expandedWidth = expandedContent + containerPadding * 2;
	const showExpanded = !isCollapsed || isHovered;

	return (
		<Box
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			sx={{
				height: '100%',
				width:
					isCollapsed && !isHovered ? bubbleSize + containerPadding * 2 : expandedWidth,
				padding: `${containerPadding}px`,
				display: 'flex',
				flexDirection: 'row',
				alignItems: 'center',
				zIndex: 1,
				transition: 'width 0.25s ease-out',
			}}
		>
			{showExpanded ? (
				<>
					<BubbleWrapper index={0} total={totalUsers} animate={isCollapsed}>
						<PresenceBubble user={myself} isSelf />
					</BubbleWrapper>
					{remoteUsers.map((user, i) => (
						<BubbleWrapper
							key={user.attendee.attendeeId}
							index={i + 1}
							total={totalUsers}
							animate={isCollapsed}
						>
							<PresenceBubble user={user} />
						</BubbleWrapper>
					))}
				</>
			) : (
				<CollapsedIndicator allUsers={allUsers} />
			)}
		</Box>
	);
}

/**
 * Wrapper that positions a bubble within the facepile stack.
 *
 * @remarks
 * Each wrapper acts as a flex slot that starts at `bubbleSize + bubbleGap` wide
 * but can shrink down to `minSlotWidth`. When the container has enough room the
 * slots sit at their natural width and bubbles appear with a small gap. When the
 * container is constrained the slots shrink and the fixed-size bubbles overlap
 * because they extend beyond their slot boundary.
 *
 * Earlier bubbles get a higher base z-index so the self bubble sits on top.
 * On hover, the z-index jumps above all others so the bubble pops to the front.
 */
function BubbleWrapper(
	props: React.PropsWithChildren<{ index: number; total: number; animate?: boolean }>,
): React.ReactElement {
	const { children, index, total, animate } = props;
	return (
		<Box
			sx={{
				position: 'relative',
				zIndex: total - index,
				flex: `0 1 ${bubbleSize + bubbleGap}px`,
				minWidth: `${minSlotWidth}px`,
				height: bubbleSize,
				transition: 'transform 0.15s ease',
				'&:hover': {
					zIndex: total + 1,
					transform: 'scale(1.1)',
				},
				...(animate
					? {
							animation: `presenceFadeIn 0.25s ease-out ${index * 0.05}s both`,
							'@keyframes presenceFadeIn': {
								from: { opacity: 0, transform: 'scale(0.8)' },
								to: { opacity: 1, transform: 'scale(1)' },
							},
						}
					: {}),
			}}
		>
			<Box sx={{ position: 'absolute', left: 0, top: 0 }}>{children}</Box>
		</Box>
	);
}

/**
 * Collapsed presence indicator shown when the viewport is too narrow for the full facepile.
 *
 * @remarks
 * Renders a single circle containing a user count. A tooltip lists all connected user names.
 */
function CollapsedIndicator(props: { allUsers: PresenceUser[] }): React.ReactElement {
	const { allUsers } = props;

	const tooltipContent = `${allUsers.length} users`;

	return (
		<Tooltip title={tooltipContent}>
			<Box
				data-testid="collapsed-indicator"
				sx={{
					width: bubbleSize,
					height: bubbleSize,
					borderRadius: '50%',
					border: '2px solid',
					borderColor: 'grey.900',
					bgcolor: 'grey.800',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					cursor: 'default',
					flexShrink: 0,
				}}
			>
				<Typography
					data-testid="collapsed-count"
					sx={{
						fontSize: counterFontSize,
						fontWeight: 'bold',
						color: 'text.primary',
						lineHeight: 1,
					}}
				>
					{allUsers.length}
				</Typography>
			</Box>
		</Tooltip>
	);
}

/**
 * Renders a single presence bubble.
 *
 * @remarks
 * Resolves each user to one of three display modes:
 * 1. Player with a session character: shows the character's image
 * 2. Game Master: shows a Security icon
 * 3. Guest/unknown: shows a Person icon
 *
 * When `isSelf` is true, the bubble is rendered with a highlight border.
 */
function PresenceBubble(props: { user: PresenceUser; isSelf?: boolean }): React.ReactElement {
	const { user, isSelf } = props;
	const appContext = useAppContext();
	const { matchedUser, character, isGameMaster } = useResolvedPresenceUser(user);

	const { currentApp } = user.value;

	// Border matching the banner background for clean overlap separation
	const borderStyle = { border: '2px solid', borderColor: 'grey.900' };

	const selfHighlight = isSelf
		? { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1 }
		: {};

	const tooltipSuffix = isSelf ? ' (you)' : '';

	// Game Master
	if (matchedUser && isGameMaster) {
		return (
			<Tooltip
				title={
					<PresenceTooltip
						icon={<Security fontSize="small" />}
						label={`Game Master${tooltipSuffix}`}
					/>
				}
			>
				<Avatar
					sx={{
						width: bubbleSize,
						height: bubbleSize,
						bgcolor: 'warning.dark',
						cursor: 'default',
						...borderStyle,
						...selfHighlight,
					}}
				>
					<Security fontSize="small" />
				</Avatar>
			</Tooltip>
		);
	}

	// Player with a character
	if (matchedUser && character) {
		const characterImage = renderCharacterImage(character, {
			variant: CharacterImageVariant.Profile,
			containerShape: ImageContainerShape.Circle,
			maxHeightInPixels: bubbleSize,
			maxWidthInPixels: bubbleSize,
		});

		return (
			<Tooltip
				title={
					<PresenceTooltip
						icon={characterImage}
						label={`${character.name}${tooltipSuffix}`}
						subtitle={currentApp}
						onClick={
							isSelf ? undefined : () => appContext.handleCharacterLink(character)
						}
					/>
				}
			>
				<Box
					sx={{
						width: bubbleSize,
						height: bubbleSize,
						borderRadius: '50%',
						overflow: 'hidden',
						cursor: isSelf ? 'default' : 'pointer',
						flexShrink: 0,
						...borderStyle,
						...selfHighlight,
					}}
					onClick={isSelf ? undefined : () => appContext.handleCharacterLink(character)}
				>
					{characterImage}
				</Box>
			</Tooltip>
		);
	}

	// Guest / unknown user
	return (
		<Tooltip
			title={
				<PresenceTooltip
					icon={<Person fontSize="small" />}
					label={`Guest${tooltipSuffix}`}
					subtitle={currentApp}
				/>
			}
		>
			<Avatar
				sx={{
					width: bubbleSize,
					height: bubbleSize,
					bgcolor: 'grey.600',
					cursor: 'default',
					...borderStyle,
					...selfHighlight,
				}}
			>
				<Person fontSize="small" />
			</Avatar>
		</Tooltip>
	);
}
